import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/apiClient";
import { Activity, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reset/${token}`).then((r) => setEmail(r.data.email))
      .catch((e) => setError(e.response?.data?.detail || "Invalid or expired reset link"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await axios.post(`${API}/reset/${token}`, { password }); setDone(true); }
    catch (e) { setError(e.response?.data?.detail || "Reset failed"); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center"><Activity className="w-5 h-5 text-white" strokeWidth={2.5} /></div>
          <span className="text-2xl font-bold tracking-tight text-slate-900 font-head">ReferU</span>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          {done ? (
            <div className="text-center"><h1 className="text-2xl font-semibold text-slate-900 font-head">Password updated</h1>
              <p className="text-slate-500 mt-2">You can now log in with your new password.</p>
              <Button data-testid="reset-go-login" onClick={() => navigate("/login")} className="mt-6 rounded-xl bg-blue-600 hover:bg-blue-700">Go to login</Button></div>
          ) : error && !email ? (
            <div className="text-center"><p className="text-red-600 font-medium" data-testid="reset-error">{error}</p>
              <Button onClick={() => navigate("/login")} className="mt-4 rounded-xl bg-blue-600 hover:bg-blue-700">Go to login</Button></div>
          ) : (
            <>
              <h1 className="text-2xl font-semibold text-slate-900 font-head">Set a new password</h1>
              <p className="text-slate-500 mt-1 text-sm">For {email}</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div><Label>New Password</Label><Input data-testid="reset-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-12 rounded-xl" /></div>
                {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
                <Button data-testid="reset-submit" type="submit" disabled={submitting} className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-700">{submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Update password"}</Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
